import numeral from 'numeral';

import type { IChannelForecast, TAdChannelKey } from '@feathr/blackbox';

/**
 * Builds the channels array from channel visibility flags.
 *
 * @param showDisplay - Whether the Display Ads channel is enabled.
 * @param showMeta - Whether the Meta Ads channel is enabled.
 * @returns Array of forecast channels to request.
 * @example
 * buildChannelsArray(true, true);  // ['tradedesk', 'meta']
 * buildChannelsArray(true, false); // ['tradedesk']
 * buildChannelsArray(false, false); // []
 */
function buildChannelsArray(showDisplay: boolean, showMeta: boolean): TAdChannelKey[] {
  const channels: TAdChannelKey[] = [];

  if (showDisplay) {
    channels.push('tradedesk');
  }

  if (showMeta) {
    channels.push('meta');
  }

  return channels;
}

/**
 * Formats a reach number for display with a tilde prefix and comma separators.
 *
 * @param reach - The reach number to format.
 * @returns Formatted string like "~45,000" or "~500".
 * @example
 * formatReachEstimate(45000); // "~45,000"
 * formatReachEstimate(500);   // "~500"
 * formatReachEstimate(0);     // "~0"
 */
function formatReachEstimate(reach: number): string {
  return `~${numeral(reach).format('0,0')}`;
}

/**
 * Parses a channel forecast response into per-channel reach data.
 *
 * Uses `min` (lower bound) for both channels since Meta's upper bound is
 * unreliable (per issue #7508: "FE should use min for the displayed estimate").
 *
 * @param response - Per-channel response with optional `tradedesk` and `meta` entries.
 * @returns Object with `displayReach` from `tradedesk.min` and `metaReach` from `meta.min`.
 *   Either value is null if the corresponding channel is absent from the response.
 * @example
 * parseChannelForecast({ tradedesk: { min: 3000, max: 6000 }, meta: { min: 1500, max: 4000 } });
 * // { displayReach: 3000, metaReach: 1500 }
 */
function parseChannelForecast(response: IChannelForecast): {
  displayReach: number | null;
  metaReach: number | null;
} {
  return {
    displayReach: response.tradedesk?.min ?? null,
    metaReach: response.meta?.min ?? null,
  };
}

export { buildChannelsArray, formatReachEstimate, parseChannelForecast };
